import { Injectable } from '@angular/core';
import {
  AddressService,
  AddressAddress,
  KycService,
  HyperwalletKycSaveCustomerPayload,
  HyperwalletKycCustomer
} from '@vestiaire/api-sdk-angular';
import { Observable, of, BehaviorSubject, throwError, forkJoin } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { IAppState } from '@app/store';
import { Store, select } from '@ngrx/store';
import * as fromUser from '@app/core/user/store/reducers';
import * as dayjs from 'dayjs';
import { environment } from '@env/environment';
import { LocaleService } from '@app/core/i18n';
import { SetBadgeMe, SetPageParams } from '@app/core/header/store';
import { TransferMethodDetails } from '../models/transfer-method';
import { Logger } from '@app/core/logging';
import { HttpClient } from '@angular/common/http';
import { SessionCookieService } from '@app/core/cookies';
import { HelpersService } from '@app/shared/services/helpers/helpers.service';

interface CreateCustomer {
  address: CustomerAddress;
  contact: { phone: string };
  personalInformation: {
    dateOfBirth: { day: string; month: string; year: string };
    firstname: string;
    lastname: string;
  };
  business: { businessName: string };
}

interface CustomerAddress {
  city: string;
  country: string;
  street: string;
  zip: string;
  state: string;
}

interface GetAddress {
  countryList: Country[];
  stateList: {
    [key: string]: any;
  };
  address: AddressAddress;
}

export interface Country {
  name: string;
  isoCode: string;
}

@Injectable({ providedIn: 'root' })
export class GetPaidService {
  private _stepsState$ = new BehaviorSubject<any>(undefined);
  stepState$ = this._stepsState$.asObservable();

  constructor(
    private addressService: AddressService,
    private store: Store<IAppState>,
    private kycService: KycService,
    private localeService: LocaleService,
    private logger: Logger,
    private http: HttpClient,
    private sessionCookieService: SessionCookieService,
    private helperService: HelpersService
  ) {}

  getUserAddress(): Observable<Omit<GetAddress, 'countryList'>> {
    return this.addressService.getAddressForm().pipe(
      map(({ data }) => {
        const { addresses, form } = data;
        const stateList = form.states;
        let address = (addresses && addresses.billing && addresses.billing[0]) || {};

        if (addresses && addresses.billing && addresses.billing[0]) {
          address = addresses.billing[0];
        }
        return {
          stateList,
          address
        };
      })
    );
  }

  /**
   * Get user address data and HW countries
   */
  getAddressFormData(): Observable<GetAddress> {
    return forkJoin({ address: this.getUserAddress(), countryList: this.getTransferMethodCountries() }).pipe(
      map(({ address, countryList }) => ({
        ...address,
        countryList
      }))
    );
  }

  createCustomer(form: CreateCustomer) {
    const { address, business, contact, personalInformation } = form;
    const res: HyperwalletKycSaveCustomerPayload = {
      customerType: HyperwalletKycSaveCustomerPayload.CustomerTypeEnum.Individual,
      address: {
        address1: address.street,
        city: address.city,
        state: address.state || '',
        zip: address.zip,
        country: address.country
      }
    };

    if (business) {
      res.customerType = HyperwalletKycSaveCustomerPayload.CustomerTypeEnum.Business;
      res.name = business.businessName;
    } else {
      res.firstName = personalInformation.firstname;
      res.lastName = personalInformation.lastname;
      res.dob = `${personalInformation.dateOfBirth.year}-${personalInformation.dateOfBirth.month}-${personalInformation.dateOfBirth.day}`;
      res['phone'] = contact.phone;
    }

    return this.kycService.saveNewCustomer(res).pipe(
      catchError(err => {
        //  IF error return errors object
        if (err && err.error && err.error.errors) {
          return throwError(err.error.errors);
        }
        return throwError(err);
      }),
      tap(({ data }) => {
        this._stepsState$.next(data);
        this.storeTransferMethodCountry(res.address.country);
        return data;
      })
    );
  }

  getCustomer() {
    return this.kycService.getCustomer().pipe(
      catchError(err => {
        //  IF 404 => Customer not created HW side yet
        if (err && err.status === 404) {
          return of(undefined);
        }
        return throwError(err);
      }),
      map(res => {
        if (res && res.data) {
          this._stepsState$.next(res.data);
          return res.data;
        }
        return undefined;
      })
    );
  }

  getUserDateOfBirth() {
    return this.store.pipe(
      select(fromUser.getUser),
      map(user => {
        if (user && user.birthDate) {
          const dateOfBirth = dayjs(user.birthDate);

          return {
            day: dateOfBirth
              .day()
              .toString()
              .padStart(2, '0'),
            month: (dateOfBirth.month() + 1).toString().padStart(2, '0'),
            year: dateOfBirth.year()
          };
        }

        return undefined;
      })
    );
  }

  userIsB2C() {
    return this.store.pipe(
      select(fromUser.getUser),
      map(user => (user && user.segment ? user.segment === 'B2C' : undefined))
    );
  }

  sendNewVerificationCode() {
    return this.sendOTPCode();
  }

  /**
   * Need action : add badge to header
   */
  getNeedAction(): Observable<boolean> {
    if (!environment.feature.hyperwallet) {
      return of(false);
    }

    return this.kycService.getActionNeeded().pipe(map(({ data }) => data.actionNeeded));
  }

  getUserLang() {
    return this.localeService.getLanguage();
  }

  postTransfertMethod(token: string, currency: string) {
    return this.kycService.postTransferMethod({
      token,
      currency
    });
  }

  sendOTPCode() {
    return this.kycService.postConfirmationCode();
  }

  checkOTPCode(code: string) {
    return this.kycService.markConfirmationCodeAsUsed(code);
  }

  getAuthToken() {
    return this.kycService.postAuthToken().toPromise();
  }

  getUserEmail() {
    return this.store.pipe(
      select(fromUser.getUser),
      map(user => user && user.email)
    );
  }

  isDesktopScreen() {
    return this.helperService.device.isMdUp();
  }

  updateKycCustomer(data: HyperwalletKycCustomer) {
    this._stepsState$.next(data);
  }

  updateBadge(data: boolean) {
    this.store.dispatch(new SetBadgeMe(data));
  }

  getTransferMethodDetails(): Observable<TransferMethodDetails> {
    return this.kycService.getTransferMethod().pipe(
      catchError(err => {
        if (err && err.status !== 404) {
          this.logger.info('[GET PAID] Current transfer method', err);
        }
        return of(undefined);
      }),
      map(res => (res && res.data ? res.data : undefined))
    );
  }

  deleteTransferMethod(): Observable<any> {
    return this.kycService.deactivateTransferMethod().pipe(
      catchError(err => {
        this.logger.info('[GET PAID] deactivateTransferMethod', err);
        return throwError(err);
      })
    );
  }

  getTransferMethodCountries() {
    const dataUrl = `${
      environment.apiStaticBaseUrl
    }${this.localeService.getRegion()}/${this.localeService.getLocale().toString()}/hyperwalltSupportedCountries.json`;
    const sortByName = (a: Country, b: Country) => a.name.localeCompare(b.name);

    return this.http.get(dataUrl).pipe(map((res: Country[]) => res.sort(sortByName)));
  }

  storeTransferMethodCountry(countryCode) {
    const userId = this.sessionCookieService.getUserId();
    localStorage.setItem(`kyc_tm_country_${userId}`, countryCode);
  }

  getTransferMethodCountry() {
    const userId = this.sessionCookieService.getUserId();
    return localStorage.getItem(`kyc_tm_country_${userId}`);
  }

  /**
   * Dispatch pageType to header
   */
  setPageParam() {
    this.store.dispatch(new SetPageParams({ pageType: 'get-paid' }));
  }

  /**
   * Check if HW feature is active
   * - check frontend feature flag
   * - check if user is whitelisted
   */
  featureIsActive(): Observable<boolean> {
    return this.store.pipe(
      select(fromUser.getUser),
      map(user => environment.feature.hyperwallet && user?.hyperwalletActive)
    );
  }
}
